import React, { useContext, useRef, useState } from 'react'
import { Button, Card, Col, Collapse, Container, Row } from 'react-bootstrap'
import { BsArrowDown, BsArrowUp } from 'react-icons/bs'
import {
  EditorContext,
  EditorContextType,
} from '@editor/components/editorContext'
import { TagModel } from '@editor/store/slices/tag.slice'
import TagForm from '@editor/components/tagBar/tagForm'
import TagList from '@editor/components/tagBar/tagList'

const TagBar = () => {
  const {
    selectedPathTags,
    selectedRange,
    setSelectedRange,
    globalTags,
    editorRef,
  } = useContext(EditorContext) as EditorContextType
  const [detailOpened, isDetailOpened] = useState(false)
  const [tagFormOpened, isTagFormOpened] = useState(false)
  const [editingTag, setEditingTag] = useState<TagModel>()
  const [editingTagIndex, setEditingTagIndex] = useState<string>()
  const target = useRef(null)

  const submitTagForm = (newTag: TagModel) => {
    if (editingTag) {
      editTagFromSelection(editingTag, newTag)
      setEditingTagIndex(undefined)
      setEditingTag(undefined)
      isTagFormOpened(false)
    } else {
      addTagToSelection(newTag)
      isTagFormOpened(false)
    }
  }

  const handleAddTag = (e: React.MouseEvent) => {
    e.preventDefault()
    setEditingTag(undefined)
    setEditingTagIndex(undefined)
    isTagFormOpened(true)
  }

  const handleCloseForm = (e: React.MouseEvent) => {
    e.preventDefault()
    setEditingTagIndex(undefined)
    setEditingTag(undefined)
    isTagFormOpened(false)
  }

  const handleRemoveTag = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    tagToRemove: TagModel
  ) => {
    event.preventDefault()
    removeTagFromSelection(tagToRemove)
  }

  const handleEditTag = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    tagToEdit: TagModel,
    tagIndex?: string
  ) => {
    event.preventDefault()
    setEditingTag(tagToEdit)
    setEditingTagIndex(tagIndex)
    isTagFormOpened(true)
  }
  const removeTagFromSelection = (tagToRemove: TagModel) => {
    // Implementation for tag removal in the DOM
    if (!tagToRemove) return

    // Fetch the DOM element corresponding to tagToRemove
    // You need a function to get the DOM element by TagModel,
    // it depends on how you maintain the relationship between the TagModel and the actual DOM element.
    const elementToRemove = getElementByTagModel(tagToRemove)

    if (elementToRemove) {
      // Reattach children to the parent
      while (elementToRemove.firstChild) {
        elementToRemove.parentNode?.insertBefore(
          elementToRemove.firstChild,
          elementToRemove
        )
      }

      // Remove the element itself
      elementToRemove.parentNode?.removeChild(elementToRemove)
    }
    if (selectedRange) {
      const newRange = document.createRange()
      newRange.setStart(selectedRange.startContainer, selectedRange.startOffset)
      newRange.setEnd(selectedRange.endContainer, selectedRange.endOffset)

      // Here we update the selectedRange state
      setSelectedRange(newRange)
    }
  }
  const addTagToSelection = (newTag: TagModel) => {
    if (!selectedRange) {
      console.log('No range is currently selected')
      return
    }

    // Create a new element using the tag elements
    const newElement = document.createElement(newTag.tagName)

    // Apply any attributes from the new tag to the new element
    for (const attr of newTag.tagAttributes) {
      newElement.setAttribute(attr.attributeName, attr.attributeValue)
    }

    // Wrap the current selection with the new tag.
    // This assumes that nothing is currently "selected" (i.e., no text is highlighted).
    newElement.textContent = selectedRange.toString()

    selectedRange.deleteContents()
    selectedRange.insertNode(newElement)

    // Update the selectedRange to reflect changes in the DOM
    const newRange = document.createRange()
    newRange.selectNodeContents(newElement)
    setSelectedRange(newRange)
  }

  const editTagFromSelection = (originalTag: TagModel, editedTag: TagModel) => {
    // Get the original element from the DOM
    const elementToEdit = getElementByTagModel(originalTag)

    if (!elementToEdit || !editedTag) {
      console.log('Element to edit or edited tag not found')
      return
    }

    // Create a new element
    const newElement = document.createElement(editedTag.tagName)

    // Apply attributes from the new tag to the new element
    for (const attr of editedTag.tagAttributes) {
      newElement.setAttribute(attr.attributeName, attr.attributeValue)
    }

    // Copy over any children from the old element
    while (elementToEdit.firstChild) {
      newElement.appendChild(elementToEdit.firstChild)
    }

    // Now replace the old element with the new one
    elementToEdit.parentNode?.replaceChild(newElement, elementToEdit)

    // If the update involves the currently selected tag
    if (selectedRange) {
      // Update the selectedRange to reflect changes in the DOM
      const newRange = document.createRange()
      newRange.selectNodeContents(newElement)
      setSelectedRange(newRange)
    }
  }

  function getElementByTagModel(tagModel: TagModel) {
    // Start from the element in the selectionRange
    if (selectedRange) {
      let currentElement: HTMLElement | null =
        selectedRange.commonAncestorContainer as HTMLElement

      // Move up through the parent elements until reaching the editor div, looking for our tag.
      while (currentElement != null && currentElement !== editorRef.current) {
        // If a matching element is found, return it
        if (isMatchingElement(currentElement, tagModel)) {
          return currentElement
        }

        // Move to the parent
        currentElement = currentElement.parentNode as HTMLElement
      }
    }
    // No match found.
    return null
  }

  function isMatchingElement(
    element: HTMLElement,
    tagModel: TagModel
  ): boolean {
    // Check if the tag name and attributes match
    return (
      element.nodeName?.toLowerCase() === tagModel.tagName &&
      isMatchingTagAttributes(element, tagModel)
    )
  }

  function isMatchingTagAttributes(
    element: HTMLElement,
    tagModel: TagModel
  ): boolean {
    // Check for attribute match
    return tagModel.tagAttributes.every((attrModel) => {
      const domAttr = element.getAttribute(attrModel.attributeName)
      return domAttr != null && domAttr === attrModel.attributeValue
    })
  }

  return selectedPathTags ? (
    <Card className="my-3 p-0">
      <Card.Header>
        <Row>
          <Col className="d-flex align-items-center">
            <Card.Title className="m-0">Tagy</Card.Title>
          </Col>
          <Col className="d-flex justify-content-end">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => isDetailOpened(!detailOpened)}
            >
              {detailOpened ? (
                <BsArrowUp></BsArrowUp>
              ) : (
                <BsArrowDown></BsArrowDown>
              )}
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Collapse in={detailOpened} unmountOnExit={false}>
        <Card.Body ref={target}>
          <Container>
            <Row className="my-2">
              <h5 className="mx-2  p-0">Globální:</h5>
            </Row>
            <TagList
              editable={false}
              tags={globalTags}
              indexPrefix={'g'}
              editingTagIndex={editingTagIndex}
              handleEditTag={handleEditTag}
              handleRemoveTag={handleRemoveTag}
            ></TagList>
          </Container>
          {selectedRange && (
            <Container>
              <Row className="my-2">
                <h5 className="mx-2 p-0">Vybraný úsek:</h5>
              </Row>
              <TagList
                editable={true}
                tags={selectedPathTags.slice(globalTags?.length)}
                indexPrefix={'d'}
                editingTagIndex={editingTagIndex}
                handleEditTag={handleEditTag}
                handleRemoveTag={handleRemoveTag}
              ></TagList>
              <Row>
                <Col className="d-flex p-0 justify-content-end">
                  {!tagFormOpened && (
                    <Button
                      variant="success"
                      className="w-auto my-1 fw-bold"
                      onClick={(e) => handleAddTag(e)}
                    >
                      Přidat Tag
                    </Button>
                  )}
                </Col>
              </Row>
            </Container>
          )}
          {tagFormOpened && (
            <TagForm
              editingTag={editingTag}
              editingIndex={editingTagIndex}
              submitTagForm={submitTagForm}
              closeForm={handleCloseForm}
            ></TagForm>
          )}
        </Card.Body>
      </Collapse>
    </Card>
  ) : (
    <></>
  )
}

export default TagBar
